import { camelize } from '@src/packages/utils';

import { COLORS_PALETTE, EXPRESSION_VALUE_TYPE, GROUP_BY_OPTIONS_VALUES } from '../../../constants';

export const transformScheduleComplianceData = (
  result,
  _filters,
  _intl,
  _chartGroupBy,
  queryParams,
) => {
  const { data: agg } = camelize(result);
  if (!agg) {
    return {
      data: {
        metrics: [],
      },
    };
  }
  const results = agg?.aggregations?.results;
  const groupQueryParamsBy = queryParams?.groupBy;

  let buckets = results?.buckets || [];

  switch (groupQueryParamsBy) {
    case GROUP_BY_OPTIONS_VALUES.GROUPS:
      buckets = results?.byGroup?.buckets || [];
      break;
    case GROUP_BY_OPTIONS_VALUES.USERS:
      buckets = results?.byUser?.buckets || [];
      break;
    default:
      break;
  }

  if (!buckets || !Array.isArray(buckets)) {
    return {
      data: {
        metrics: [],
      },
    };
  }

  let resultData = [];

  switch (groupQueryParamsBy) {
    case GROUP_BY_OPTIONS_VALUES.DIVISIONS:
      resultData = getResultsByDivisions(buckets, queryParams?.valueType);
      break;
    case GROUP_BY_OPTIONS_VALUES.GROUPS:
      resultData = getResultsByGroups(buckets, queryParams?.valueType);
      break;
    case GROUP_BY_OPTIONS_VALUES.USERS:
      resultData = getResultsByUsers(buckets, queryParams?.valueType);
      break;
    case GROUP_BY_OPTIONS_VALUES.SCHEDULES:
    case GROUP_BY_OPTIONS_VALUES.ROUTES:
    case GROUP_BY_OPTIONS_VALUES.TASKS:
      resultData = getResultsBySchedules(buckets, queryParams?.valueType);
      break;
    case GROUP_BY_OPTIONS_VALUES.TEMPLATES:
      resultData = getResultsByTemplates(buckets, queryParams?.valueType);
      break;
    default:
      break;
  }

  return {
    data: {
      metrics: resultData,
    },
  };
};

const ensureUniqueKey = (buckets, getBaseKey) => {
  const keysCount = {};
  buckets.forEach(bucket => {
    const baseKey = getBaseKey(bucket) || '';
    keysCount[baseKey] = (keysCount[baseKey] || 0) + 1;
  });
  return (bucket, baseKey, index) => {
    if (keysCount[baseKey] <= 1) return baseKey;
    const uniqueId = bucket.key ?? index;
    return `${baseKey} (${uniqueId})`;
  };
};

const getResultsByDivisions = (buckets, valueType) => {
  const getBaseKey = b => b?.division?.hits?.hits?.[0]?.source?.division?.name;
  const resolveKey = ensureUniqueKey(buckets, getBaseKey);

  return buckets.map((bucket, index) => {
    const baseKey = getBaseKey(bucket);
    const key = resolveKey(bucket, baseKey, index);
    const value =
      valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE
        ? bucket?.totalCompletionPercentage?.value
        : bucket?.triggersNested?.totalFinished?.value;

    return {
      key,
      label: baseKey,
      value: Math.round(value || 0),
      color: COLORS_PALETTE[index % COLORS_PALETTE.length],
    };
  });
};

const getResultsByGroups = (buckets, valueType) => {
  const getBaseKey = b => b?.group?.hits?.hits?.[0]?.source?.name;
  const resolveKey = ensureUniqueKey(buckets, getBaseKey);

  return buckets.map((bucket, index) => {
    const baseKey = getBaseKey(bucket);
    const key = resolveKey(bucket, baseKey, index);
    const triggerMetrics = bucket?.toRoot?.triggersMetrics?.value;
    const value =
      valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE
        ? triggerMetrics?.totalCompletionPercentage
        : triggerMetrics?.totalFinished;

    return {
      key,
      label: baseKey,
      value: Math.round(value || 0),
      color: COLORS_PALETTE[index % COLORS_PALETTE.length],
    };
  });
};

const getResultsByUsers = (buckets, valueType) => {
  const getBaseKey = b => {
    const source = b?.user?.hits?.hits?.[0]?.source;
    return `${source?.firstName || ''} ${source?.lastName || ''}`.trim();
  };
  const resolveKey = ensureUniqueKey(buckets, getBaseKey);

  return buckets.map((bucket, index) => {
    const baseKey = getBaseKey(bucket);
    const key = resolveKey(bucket, baseKey, index);
    const triggerMetrics = bucket?.toRoot?.triggersMetrics?.value;
    const value =
      valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE
        ? triggerMetrics?.totalCompletionPercentage
        : triggerMetrics?.totalFinished;

    return {
      key,
      label: baseKey,
      value: Math.round(value || 0),
      color: COLORS_PALETTE[index % COLORS_PALETTE.length],
    };
  });
};

const getResultsBySchedules = (buckets, valueType) => {
  const getBaseKey = b => b?.schedule?.hits?.hits?.[0]?.source?.schedule?.name;
  const resolveKey = ensureUniqueKey(buckets, getBaseKey);

  return buckets.map((bucket, index) => {
    const baseKey = getBaseKey(bucket);
    const key = resolveKey(bucket, baseKey, index);
    const value =
      valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE
        ? bucket?.totalCompletionPercentage?.value
        : bucket?.triggersNested?.totalFinished?.value;

    return {
      key,
      label: baseKey,
      value: Math.round(value || 0),
      color: COLORS_PALETTE[index % COLORS_PALETTE.length],
    };
  });
};

const getResultsByTemplates = (buckets, valueType) => {
  const getBaseKey = b =>
    b?.template?.hits?.hits?.[0]?.source?.schedule?.taskSummary?.name;
  const resolveKey = ensureUniqueKey(buckets, getBaseKey);

  return buckets.map((bucket, index) => {
    const baseKey = getBaseKey(bucket);
    const key = resolveKey(bucket, baseKey, index);
    const value =
      valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE
        ? bucket?.totalCompletionPercentage?.value
        : bucket?.triggersNested?.totalFinished?.value;
    return {
      key,
      label: baseKey,
      value: Math.round(value || 0),
      color: COLORS_PALETTE[index % COLORS_PALETTE.length],
    };
  });
};
